import { apiRequest, buildApiUrl, getHeaders } from '../utils/api';
import { validateHeatmapDateRange, isHeatmapEntitlementDenied, withDistributedLockRetry } from './heatmap.service';

export { isHeatmapEntitlementDenied };

export const DEFAULT_MINING_CONFIG = {
  clusters: 2
};

export const MINING_LIMITS = {
  clusters: { min: 1, max: 8 }
};

export const FUNNEL_ACTION_LIMITS = { min: 2, max: 16 };
export const FLOW_NAME_MAX_LEN = 200;
export const FLOW_DESC_MAX_LEN = 2000;

export function clampValue(val, min, max, defaultVal) {
  const n = Number(val);
  if (Number.isNaN(n)) return defaultVal;
  return Math.min(Math.max(n, min), max);
}

export function isValidUuid(str) {
  if (!str || typeof str !== 'string') return false;
  const s = str.trim();
  if (s === '00000000-0000-0000-0000-000000000000') return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

export function sanitizeAnalyticsQuery(q = {}) {
  const queryPayload = {
    fromDate: q.fromDate,
    toDate: q.toDate
  };

  if (q.pageId && isValidUuid(q.pageId)) {
    queryPayload.pageId = q.pageId;
  }
  if (q.flowId && isValidUuid(q.flowId)) {
    queryPayload.flowId = q.flowId;
  }
  if (q.device && q.device !== 'all') {
    const d = String(q.device).toLowerCase().trim().slice(0, 16);
    if (['desktop', 'mobile', 'tablet', 'unknown'].includes(d)) {
      queryPayload.device = d;
    }
  }
  if (q.browser) {
    const b = String(q.browser).toLowerCase().trim().slice(0, 16);
    if (['chrome', 'edge', 'firefox', 'safari', 'other', 'unknown'].includes(b)) {
      queryPayload.browser = b;
    }
  }
  if (q.country) {
    const c = String(q.country).trim();
    if (/^[A-Za-z]{2}$/.test(c)) {
      queryPayload.country = c.toUpperCase();
    } else if (c.toLowerCase() === 'unknown') {
      queryPayload.country = 'unknown';
    }
  }

  return queryPayload;
}

/**
 * Fetches multi-dimensional customer behavioral breakdown report (device, browser, country).
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @param {object} options
 * @param {string} options.fromDate - Start date (YYYY-MM-DD)
 * @param {string} options.toDate - Exclusive end date (YYYY-MM-DD)
 * @param {string} [options.device] - Optional device filter (e.g. 'desktop', 'mobile')
 * @param {string} [options.browser] - Optional browser filter (e.g. 'chrome', 'safari')
 * @param {string} [options.country] - Optional ISO country code filter (e.g. 'US')
 * @returns {Promise<{ interactions: number, uniqueSessions: number, daily: Array, segments: Array }>}
 */
export const getCustomerReport = async (organizationId, siteCollectionId, {
  fromDate,
  toDate,
  device,
  browser,
  country,
  signal
} = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');
  validateHeatmapDateRange(fromDate, toDate);

  const params = new URLSearchParams({ fromDate, toDate });
  if (device) params.set('device', device);
  if (browser) params.set('browser', browser);
  if (country) params.set('country', country);

  const endpoint = `/customer-analytics/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/report?${params.toString()}`;

  return withDistributedLockRetry(async () => {
    const response = await apiRequest(buildApiUrl(endpoint), {
      method: 'GET',
      headers: getHeaders(),
      ...(signal ? { signal } : {})
    });
    return response.json();
  }, signal);
};

/**
 * Fetches conversion funnel metrics for an ordered list of journey actions.
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @param {object} options
 * @param {string} [options.fromDate] - Start date (YYYY-MM-DD)
 * @param {string} [options.toDate] - Exclusive end date (YYYY-MM-DD)
 * @param {string[]} [options.actionIds] - Ordered list of action UUIDs
 * @param {object} [options.query] - Optional query filter object
 * @param {string} [options.pageId]
 * @param {string} [options.flowId]
 * @param {string} [options.device]
 * @param {string} [options.browser]
 * @param {string} [options.country]
 * @returns {Promise<{ journeys: number, steps: Array<{ actionId: string, reached: number, dropped: number, conversionPercent: number }> }>}
 */
export const getConversionFunnel = async (organizationId, siteCollectionId, {
  fromDate,
  toDate,
  actionIds = [],
  pageId,
  flowId,
  device,
  browser,
  country,
  query,
  signal
} = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');

  const resolvedActionIds = Array.isArray(actionIds) ? actionIds : [];
  if (resolvedActionIds.length < FUNNEL_ACTION_LIMITS.min || resolvedActionIds.length > FUNNEL_ACTION_LIMITS.max) {
    throw new Error(`Funnel actionIds count must be between ${FUNNEL_ACTION_LIMITS.min} and ${FUNNEL_ACTION_LIMITS.max} (received ${resolvedActionIds.length})`);
  }

  const q = query || { fromDate, toDate, pageId, flowId, device, browser, country };
  validateHeatmapDateRange(q.fromDate, q.toDate);

  const queryPayload = sanitizeAnalyticsQuery(q);

  const endpoint = `/customer-analytics/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/funnel`;

  return withDistributedLockRetry(async () => {
    const response = await apiRequest(buildApiUrl(endpoint), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        query: queryPayload,
        actionIds: resolvedActionIds
      }),
      ...(signal ? { signal } : {})
    });
    return response.json();
  }, signal);
};

/**
 * Compares customer interactions and unique sessions between two distinct date periods / site collections.
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @param {object} options
 * @param {object} [options.left] - Prior period { fromDate, toDate }
 * @param {object} [options.right] - Current period { fromDate, toDate }
 * @param {string} [options.rightSiteCollectionId] - Optional site collection for cross-site comparison
 * @param {string} [options.leftFromDate] - Prior period start date (YYYY-MM-DD)
 * @param {string} [options.leftToDate] - Prior period end date (YYYY-MM-DD)
 * @param {string} [options.rightFromDate] - Current period start date (YYYY-MM-DD)
 * @param {string} [options.rightToDate] - Current period end date (YYYY-MM-DD)
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ left: object, right: object, interactionDelta: number, changePercent: number }>}
 */
export const getPeriodComparison = async (organizationId, siteCollectionId, {
  left,
  right,
  rightSiteCollectionId,
  leftFromDate,
  leftToDate,
  rightFromDate,
  rightToDate,
  signal
} = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');

  const leftObj = left || { fromDate: leftFromDate, toDate: leftToDate };
  const rightObj = right || { fromDate: rightFromDate, toDate: rightToDate };

  validateHeatmapDateRange(leftObj.fromDate, leftObj.toDate);
  validateHeatmapDateRange(rightObj.fromDate, rightObj.toDate);

  const payload = {
    left: {
      fromDate: leftObj.fromDate,
      toDate: leftObj.toDate
    },
    rightSiteCollectionId: rightSiteCollectionId || siteCollectionId,
    right: {
      fromDate: rightObj.fromDate,
      toDate: rightObj.toDate
    }
  };

  const endpoint = `/customer-analytics/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/compare`;

  return withDistributedLockRetry(async () => {
    const response = await apiRequest(buildApiUrl(endpoint), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
      ...(signal ? { signal } : {})
    });
    return response.json();
  }, signal);
};

/**
 * Discovers candidate journey sequences and clusters discovered by ML.NET mining engine.
 * The server automatically discovers optimal cluster count (k) via Silhouette scoring.
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @param {object} options
 * @param {string} [options.fromDate] - Start date (YYYY-MM-DD)
 * @param {string} [options.toDate] - Exclusive end date (YYYY-MM-DD)
 * @param {string} [options.pageId] - Deprecated / ignored by backend (candidate mining is cross-page)
 * @param {object} [options.query]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{ configurationRevision: number, journeys: number, clusters: Array }>}
 */
export const getJourneyMining = async (organizationId, siteCollectionId, {
  fromDate,
  toDate,
  pageId,
  flowId,
  device,
  browser,
  country,
  query,
  signal
} = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');

  const q = query || { fromDate, toDate, flowId, device, browser, country };
  validateHeatmapDateRange(q.fromDate, q.toDate);

  // Golden flow discovery spans multi-page journeys across the entire site collection;
  // ensure pageId is null in queryPayload sent to the backend.
  const queryPayload = sanitizeAnalyticsQuery({ ...q, pageId: null });

  const payload = {
    query: queryPayload
  };

  const endpoint = `/customer-analytics/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/candidates`;

  return withDistributedLockRetry(async () => {
    const response = await apiRequest(buildApiUrl(endpoint), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify(payload),
      ...(signal ? { signal } : {})
    });
    return response.json();
  }, signal);
};

/**
 * Creates a pending UserFlow from an ML-discovered journey candidate.
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @param {object} payload - CustomerFlowGenerationRequest
 * @param {string} [payload.name] - Name of the proposed user flow
 * @param {string} [payload.flowName]
 * @param {string} payload.candidateId - Discovered candidate identifier (64-char hex)
 * @param {string} [payload.confirmationId] - Client generated UUID
 * @param {boolean} [payload.confirmed=true]
 * @param {number} payload.configurationRevision - Exact revision from mining response
 * @param {string} [payload.flowDescription]
 * @param {object} payload.mining - Mining request containing exact query used for discovery
 * @param {object} payload.mining.query
 * @param {string} payload.mining.query.fromDate
 * @param {string} payload.mining.query.toDate
 * @param {Array} [payload.actions] - Mapped action definitions
 * @param {string[]} [payload.actionIds] - Action sequence UUIDs fallback
 * @returns {Promise<{ flowId: string, status: string }>}
 */
export const generateFlowFromCandidate = async (organizationId, siteCollectionId, payload = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');

  const {
    flowName,
    name,
    candidateId,
    confirmationId,
    confirmed = true,
    configurationRevision,
    flowDescription = '',
    mining,
    actions = [],
    actionIds = []
  } = payload;

  const resolvedName = (flowName || name || '').trim().slice(0, FLOW_NAME_MAX_LEN);
  if (!resolvedName) {
    throw new Error('Flow name is required');
  }
  if (!candidateId) {
    throw new Error('candidateId is required');
  }

  const normalizedCandidateId = String(candidateId).trim().toUpperCase();
  if (!/^[A-F0-9]{64}$/.test(normalizedCandidateId)) {
    throw new Error('candidateId must be a 64-character hexadecimal hash');
  }

  const resolvedRevision = configurationRevision !== undefined && configurationRevision !== null
    ? Number(configurationRevision)
    : undefined;

  if (resolvedRevision === undefined || Number.isNaN(resolvedRevision)) {
    throw new Error('configurationRevision is required');
  }

  const resolvedConfirmationId = confirmationId || (
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        })
  );

  let rawActionItems = [];
  if (Array.isArray(actions) && actions.length > 0) {
    rawActionItems = actions;
  } else if (Array.isArray(actionIds) && actionIds.length > 0) {
    rawActionItems = actionIds;
  }

  const normalizedActions = rawActionItems.map(item => {
    const actionId = typeof item === 'string' ? item : (item.actionId || item.id);
    const uxExperienceId = item && item.uxExperienceId ? item.uxExperienceId : null;
    const trigger = item && item.trigger !== undefined ? item.trigger : null;
    return {
      actionId,
      uxExperienceId,
      trigger
    };
  });

  const miningQuery = mining?.query;
  if (!miningQuery || !miningQuery.fromDate || !miningQuery.toDate) {
    throw new Error('mining.query with fromDate and toDate is required');
  }
  validateHeatmapDateRange(miningQuery.fromDate, miningQuery.toDate);

  const queryPayload = sanitizeAnalyticsQuery({
    ...miningQuery,
    pageId: null // mining queries are cross-page
  });

  const body = {
    confirmationId: resolvedConfirmationId,
    confirmed: Boolean(confirmed),
    configurationRevision: resolvedRevision,
    candidateId: normalizedCandidateId,
    flowName: resolvedName,
    flowDescription: (flowDescription || `Auto-generated from candidate ${normalizedCandidateId.substring(0, 8)}`).trim().slice(0, FLOW_DESC_MAX_LEN),
    mining: {
      query: queryPayload
    },
    actions: normalizedActions
  };

  const endpoint = `/customer-analytics/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/candidates/generate-flow`;

  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body)
  });

  return response.json();
};

/**
 * Retrieves behavioral journey checkpoint paths, loops, and detours.
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @param {object} options
 * @param {string} [options.fromDate]
 * @param {string} [options.toDate]
 * @param {string} [options.pageId]
 * @param {string} [options.flowId]
 * @param {string} [options.device]
 * @param {string} [options.browser]
 * @param {string} [options.country]
 * @param {object} [options.query]
 * @param {string[]} [options.actionIds]
 * @returns {Promise<object>}
 */
export const getCheckpoints = async (organizationId, siteCollectionId, {
  fromDate,
  toDate,
  pageId,
  flowId,
  device,
  browser,
  country,
  query,
  actionIds = [],
  signal
} = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');

  const resolvedActionIds = Array.isArray(actionIds) ? actionIds : [];
  if (resolvedActionIds.length < FUNNEL_ACTION_LIMITS.min || resolvedActionIds.length > FUNNEL_ACTION_LIMITS.max) {
    throw new Error(`Checkpoint actionIds count must be between ${FUNNEL_ACTION_LIMITS.min} and ${FUNNEL_ACTION_LIMITS.max} (received ${resolvedActionIds.length})`);
  }

  const q = query || { fromDate, toDate, pageId, flowId, device, browser, country };
  validateHeatmapDateRange(q.fromDate, q.toDate);

  const queryPayload = sanitizeAnalyticsQuery(q);

  const payload = {
    query: queryPayload,
    actionIds: resolvedActionIds
  };

  const endpoint = `/customer-analytics/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/checkpoints/review`;

  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(payload),
    ...(signal ? { signal } : {})
  });

  return response.json();
};

/**
 * Exports raw, session-free behavioral compliance analytics for the site collection.
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @param {object} options
 * @param {string} options.fromDate 
 * @param {string} options.toDate 
 * @param {string} [options.pageId] 
 * @param {string} [options.flowId] 
 * @param {string} [options.device] 
 * @param {string} [options.browser] 
 * @param {string} [options.country] 
 * @returns {Promise<object>}
 */
export const exportCustomerAnalyticsData = async (organizationId, siteCollectionId, {
  fromDate,
  toDate,
  pageId,
  flowId,
  device,
  browser,
  country
} = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');
  validateHeatmapDateRange(fromDate, toDate);

  const params = new URLSearchParams({ fromDate, toDate });
  if (pageId && pageId.trim() !== '' && pageId.trim() !== '00000000-0000-0000-0000-000000000000') params.set('pageId', pageId);
  if (flowId && flowId.trim() !== '' && flowId.trim() !== '00000000-0000-0000-0000-000000000000') params.set('flowId', flowId);
  if (device) params.set('device', device);
  if (browser) params.set('browser', browser);
  if (country) params.set('country', country);

  const endpoint = `/customer-analytics/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/interactions/export?${params.toString()}`;

  const response = await apiRequest(buildApiUrl(endpoint), {
    method: 'GET',
    headers: getHeaders()
  });

  return response.json();
};

/**
 * Records the dedicated customer_analytics.viewed typed receipt to the backend.
 * Dedicated endpoint per Capabilities v1.7.
 * @param {string} organizationId 
 * @param {string} siteCollectionId 
 * @returns {Promise<void>}
 */
export const recordCustomerAnalyticsViewed = async (organizationId, siteCollectionId) => {
  if (!organizationId || !siteCollectionId) return;

  try {
    const now = new Date();
    const datePrefix = now.toISOString().slice(0, 10).replace(/-/g, ''); // yyyyMMdd
    const uuid = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
    const eventId = `${datePrefix}:${uuid}`;
    const occurredAtUtc = now.toISOString();

    const endpoint = `/customer-analytics/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/viewed`;

    await apiRequest(buildApiUrl(endpoint), {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        eventId,
        occurredAtUtc
      })
    });
  } catch (err) {
    console.warn('[CustomerAnalytics] Failed to record customer_analytics.viewed receipt:', err?.message || err);
  }
};

/**
 * Fetches friction signals summary and detected interaction anomalies.
 * (API #18)
 * @param {string} organizationId
 * @param {string} siteCollectionId
 * @param {object} options
 * @param {string} options.fromDate - Start date (YYYY-MM-DD)
 * @param {string} options.toDate - Exclusive end date (YYYY-MM-DD)
 * @param {string} [options.pageId] - Optional page ID filter
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{
 *   organizationId: string,
 *   siteCollectionId: string,
 *   fromDate: string,
 *   toDate: string,
 *   totalFrictionEvents: number,
 *   signals: Array<{
 *     frictionId: string,
 *     signalType: 'rage_click' | 'dead_click' | 'stall' | 'repeated_validation',
 *     count: number,
 *     uniqueSessions: number,
 *     pageId?: string,
 *     targetSelector?: string,
 *     severity?: 'low' | 'medium' | 'high',
 *     firstObservedAtUtc?: string,
 *     lastObservedAtUtc?: string
 *   }>
 * }>}
 */
export const getFrictionSignals = async (organizationId, siteCollectionId, {
  fromDate,
  toDate,
  pageId,
  signal
} = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');
  validateHeatmapDateRange(fromDate, toDate);

  const params = new URLSearchParams({ fromDate, toDate });
  if (pageId && pageId.trim() !== '' && pageId.trim() !== '00000000-0000-0000-0000-000000000000') params.set('pageId', pageId);

  const endpoint = `/customer-analytics/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/friction?${params.toString()}`;

  return withDistributedLockRetry(async () => {
    const response = await apiRequest(buildApiUrl(endpoint), {
      method: 'GET',
      headers: getHeaders(),
      ...(signal ? { signal } : {})
    });
    return response.json();
  }, signal);
};

/**
 * Fetches detailed forensic evidence for a specific detected friction signal.
 * (API #19)
 * @param {string} organizationId
 * @param {string} siteCollectionId
 * @param {string} frictionId
 * @param {object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<{
 *   frictionId: string,
 *   signalType: string,
 *   ruleId: string,
 *   ruleVersion: string,
 *   reason: string,
 *   confidence: number,
 *   uncertainty: number,
 *   supportingEventIds: string[],
 *   sampleSessionIds?: string[]
 * }>}
 */
export const getFrictionEvidence = async (organizationId, siteCollectionId, frictionId, { signal } = {}) => {
  if (!organizationId) throw new Error('organizationId is required');
  if (!siteCollectionId) throw new Error('siteCollectionId is required');
  if (!frictionId) throw new Error('frictionId is required');

  const endpoint = `/customer-analytics/organizations/${encodeURIComponent(organizationId)}/site-collections/${encodeURIComponent(siteCollectionId)}/friction/${encodeURIComponent(frictionId)}/evidence`;

  return withDistributedLockRetry(async () => {
    const response = await apiRequest(buildApiUrl(endpoint), {
      method: 'GET',
      headers: getHeaders(),
      ...(signal ? { signal } : {})
    });
    return response.json();
  }, signal);
};
